import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck, faCircleXmark, faClock, faMinus, faTriangleExclamation } from '@/fontawesome';
import SymbolSourceBadge from '@/components/admin/common/SymbolSourceBadge';
import { SYMBOL_SOURCE_STATUS_LABELS } from '@/models';
import type { SymbolSourceRef, SymbolSourceStatus } from '@/models';
import { GOOGLE_SHEETS_SKIPPED_HINT, SYMBOL_PRICE_SOURCE_LABELS, SYMBOL_PRICE_SOURCE_SHORT_LABELS, symbolSourceTooltip } from '@/utils/symbol-source';
import type { SymbolPriceSource } from '@/utils/symbol-source';

// Same colors as SymbolSourceBadge, for the icon-only variant in narrow table cells
const STATUS_ICONS: Record<SymbolSourceStatus, { icon: IconDefinition; className: string }> = {
  OK: { icon: faCircleCheck, className: 'text-green-500 dark:text-green-400' },
  NOT_FOUND: { icon: faCircleXmark, className: 'text-orange-500 dark:text-orange-400' },
  ERROR: { icon: faTriangleExclamation, className: 'text-red-500 dark:text-red-400' },
  PENDING: { icon: faClock, className: 'text-blue-500 dark:text-blue-400' },
  UNSET: { icon: faMinus, className: 'text-gray-400 dark:text-gray-500' },
};

const SKIPPED_ICON = { icon: faMinus, className: 'text-gray-300 dark:text-gray-600' };

interface Props {
  source: SymbolPriceSource;
  value: SymbolSourceRef;
  /** Short source label and a status icon instead of the status badge. */
  short?: boolean;
  /** The source is not asked at the moment (Google Sheets while EODHD delivers live quotes). */
  skipped?: boolean;
}

/** One line "<source> <ref> <status>" with the check timestamps and the last error as tooltip. */
export default function SymbolSourceSummary({ source, value, short = false, skipped = false }: Props) {
  const label = short ? SYMBOL_PRICE_SOURCE_SHORT_LABELS[source] : SYMBOL_PRICE_SOURCE_LABELS[source];
  const statusIcon = skipped ? SKIPPED_ICON : STATUS_ICONS[value.status];
  const tooltip = symbolSourceTooltip(SYMBOL_PRICE_SOURCE_LABELS[source], value) + (skipped ? `\n${GOOGLE_SHEETS_SKIPPED_HINT}` : '');

  return (
    <div
      className="flex items-center gap-2 text-xs cursor-help"
      title={tooltip}
    >
      <span className={`shrink-0 font-medium text-gray-500 dark:text-gray-400 ${short ? 'w-11' : ''}`}>
        {label}
      </span>
      {short && statusIcon !== undefined ? (
        <FontAwesomeIcon
          icon={statusIcon.icon}
          className={`w-3.5 h-3.5 shrink-0 ${statusIcon.className}`}
          aria-label={skipped ? GOOGLE_SHEETS_SKIPPED_HINT : SYMBOL_SOURCE_STATUS_LABELS[value.status]}
        />
      ) : null}
      <span className={`font-mono truncate max-w-[9rem] ${value.ref !== null && !skipped ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
        {value.ref ?? '—'}
      </span>
      {!short ? <SymbolSourceBadge status={value.status} /> : null}
    </div>
  );
}
